/**
 * OPLYX — Iter 117 — Connectivity & Sync hooks
 *
 * useConnectivity()
 *   - Combines `navigator.onLine` with periodic heartbeat to /api/health
 *   - State: { online: bool, lastPing: ISO|null, latencyMs: number|null }
 *   - Heartbeat every 15s. If 2 consecutive heartbeats fail (or onLine=false),
 *     considered OFFLINE.
 *
 * useSyncQueue()
 *   - Watches pending_sales count, runs flush every 8s while online
 *   - Returns { pendingCount, syncing, lastSync, flushNow() }
 *
 * useNfceSyncQueue()
 *   - Watches pending_nfces count, retries via backend every 8s while online
 *   - Returns { pendingCount, syncing, flushNow() }
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  countPendingSales,
  listPendingSales,
  removePendingSale,
  countPendingNfces,
  listPendingNfces,
  removePendingNfce,
  getAllPendingMutations,
  ackTabMutation,
  saveTab,
  deleteTab,
  countPendingTabMutations,
} from "@/lib/offline_db";

// Module-level tab ID remap: "offline_XXX" → server id (survives React re-renders)
const _tabIdMap = new Map();
let _tabSyncRunning = false;

const HEARTBEAT_MS = 15000;
const SYNC_MS = 8000;

export function useConnectivity() {
  const [state, setState] = useState({
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    lastPing: null,
    latencyMs: null,
  });
  const failsRef = useRef(0);

  const ping = useCallback(async () => {
    const t0 = performance.now();
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
      const r = await fetch(url, { method: "GET", cache: "no-store" });
      if (!r.ok) throw new Error("non-2xx");
      const latency = Math.round(performance.now() - t0);
      failsRef.current = 0;
      setState({ online: true, lastPing: new Date().toISOString(), latencyMs: latency });
    } catch {
      failsRef.current += 1;
      // 2 consecutive failures or browser says offline → OFFLINE
      if (failsRef.current >= 2 || (typeof navigator !== "undefined" && !navigator.onLine)) {
        setState((s) => ({ ...s, online: false, latencyMs: null }));
      }
    }
  }, []);

  useEffect(() => {
    ping(); // initial
    const id = setInterval(ping, HEARTBEAT_MS);
    const onOnline  = () => ping();
    const onOffline = () => setState((s) => ({ ...s, online: false }));
    if (typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
    }
    return () => {
      clearInterval(id);
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      }
    };
  }, [ping]);

  return state;
}

export function useSyncQueue(online) {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const runningRef = useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      const c = await countPendingSales();
      setPendingCount(c);
    } catch { /* idb not ready yet */ }
  }, []);

  const flushNow = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setSyncing(true);
    try {
      const queued = await listPendingSales();
      if (queued.length === 0) return;
      // Process oldest first
      queued.sort((a, b) => (a._queued_at || "").localeCompare(b._queued_at || ""));
      for (const sale of queued) {
        try {
          // Strip our internal fields before POSTing
          const { _queued_at, _local_id, ...payload } = sale;
          await api.post("/sales", payload);
          await removePendingSale(sale.idempotency_key);
        } catch (e) {
          // 4xx (validation) → drop from queue to prevent infinite loop and notify
          // 5xx / network → keep in queue, will retry next interval
          const status = e?.response?.status;
          if (status && status >= 400 && status < 500) {
            // Operator needs to know — but don't crash the loop
            console.error("[sync] dropped sale due to validation error", status, sale.idempotency_key);
            await removePendingSale(sale.idempotency_key);
          } else {
            // network error — stop, retry later
            break;
          }
        }
      }
      setLastSync(new Date().toISOString());
    } finally {
      runningRef.current = false;
      setSyncing(false);
      refreshCount();
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    if (!online) return;
    const id = setInterval(() => { flushNow(); }, SYNC_MS);
    flushNow(); // immediate when going online
    return () => clearInterval(id);
  }, [online, flushNow, refreshCount]);

  return { pendingCount, syncing, lastSync, flushNow, refreshCount };
}

export function useNfceSyncQueue(online) {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const runningRef = useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      const c = await countPendingNfces();
      setPendingCount(c);
    } catch { /* idb not ready yet */ }
  }, []);

  const flushNow = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setSyncing(true);
    try {
      const queued = await listPendingNfces();
      if (!queued.length) return;
      queued.sort((a, b) => (a.queued_at || "").localeCompare(b.queued_at || ""));
      for (const nfce of queued) {
        try {
          await api.post("/fiscal/nfce", { sale_id: nfce.sale_id });
          await removePendingNfce(nfce.idempotency_key);
        } catch (e) {
          const status = e?.response?.status;
          if (status && status >= 400 && status < 500) {
            // Permanent error (fiscal config missing, sale not found) — drop to avoid loop
            console.error("[nfce-sync] dropped entry:", status, nfce.idempotency_key);
            await removePendingNfce(nfce.idempotency_key);
          } else {
            break; // network / server error — retry next interval
          }
        }
      }
    } finally {
      runningRef.current = false;
      setSyncing(false);
      refreshCount();
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    if (!online) return;
    const id = setInterval(flushNow, SYNC_MS);
    flushNow();
    return () => clearInterval(id);
  }, [online, flushNow, refreshCount]);

  return { pendingCount, syncing, flushNow, refreshCount };
}

export function useTabSyncQueue(online) {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const c = await countPendingTabMutations();
      setPendingCount(c);
    } catch { /* idb not ready yet */ }
  }, []);

  const flushNow = useCallback(async () => {
    if (_tabSyncRunning) return;
    _tabSyncRunning = true;
    setSyncing(true);
    try {
      const mutations = await getAllPendingMutations();
      if (!mutations.length) return;
      mutations.sort((a, b) => (a._queued_at || "").localeCompare(b._queued_at || ""));
      for (const mut of mutations) {
        try {
          let realTabId = mut.tab_id;
          if (typeof realTabId === "string" && realTabId.startsWith("offline_")) {
            realTabId = _tabIdMap.get(realTabId);
            if (!realTabId && mut.type !== "create_tab") {
              // Dependency on a create_tab that hasn't synced yet — stop and retry later
              break;
            }
          }

          if (mut.type === "create_tab") {
            const { data } = await api.post("/tabs", mut.payload);
            _tabIdMap.set(mut.tab_id, data.id);
            await deleteTab(mut.tab_id);
            await saveTab({ ...data, _synced: true });
          } else if (mut.type === "add_items") {
            await api.post(`/tabs/${realTabId}/items`, mut.payload);
          } else if (mut.type === "close_tab") {
            const payload = { ...mut.payload };
            if (payload.payment_method === "card_offline") payload.payment_method = "card";
            await api.post(`/tabs/${realTabId}/close`, payload);
            await deleteTab(mut.tab_id);
          } else if (mut.type === "cancel_tab") {
            await api.post(`/tabs/${realTabId}/cancel`, mut.payload);
            await deleteTab(mut.tab_id);
          }

          await ackTabMutation(mut._mutation_id);
        } catch (e) {
          const status = e?.response?.status;
          if (status && status >= 400 && status < 500) {
            console.error("[tab-sync] dropped mutation (client error):", status, mut._mutation_id, mut.type);
            await ackTabMutation(mut._mutation_id);
          } else {
            break; // network / server error — retry next interval
          }
        }
      }
    } finally {
      _tabSyncRunning = false;
      setSyncing(false);
      refreshCount();
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    if (!online) return;
    const id = setInterval(flushNow, SYNC_MS);
    flushNow();
    return () => clearInterval(id);
  }, [online, flushNow, refreshCount]);

  return { pendingCount, syncing, flushNow, refreshCount };
}
